import { useState } from 'react';
import { useMount } from '@simplywallst/ui-core';
import { differenceInSeconds, parseISO, add } from 'date-fns';

import type { ReactNode } from 'react';
import type { Duration } from 'date-fns';

interface Props {
  expiresIn: Duration;
  startedFrom: string;
  children?: ReactNode;
}

/**
 * A component that renders until a certain duration has elapsed since a given start time.
 * @param expiresIn - The duration after which the component should stop rendering.
 * @param startedFrom - The ISO 8601 timestamp (e.g. '2022-04-01T10:00:00.000Z') from which the countdown starts.
 * @param children - The component element to render while the component is still rendering.
 * @returns The component element to render, or null if the component should stop rendering.
 */
export function RenderUntilExpiry({ expiresIn, startedFrom, children }: Props) {
  const startedTime = parseISO(startedFrom);
  const expiryTime = add(startedTime, expiresIn);
  const [isRendering, setIsRendering] = useState(false);

  useMount(() => {
    const currentTime = new Date();
    const diff = differenceInSeconds(currentTime, expiryTime);
    if (diff < 0) {
      setIsRendering(true);
    }
  });
  return isRendering ? <>{children}</> : null;
}
